import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink, MoreHorizontal, Copy, Clipboard, Check, Eye, Download, Search, Upload, FileSpreadsheet, AlertCircle } from "lucide-react";
import { FileUpload } from "@/App/Distributions/components/FileUpload";
import { Mask } from "@/utils/mask";
import { getShowContests } from "@/Api/contests";
import { getSelectUnities } from "@/Api/unities";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SharingBtn } from "@/components/Sharing";
import { CreateLoaderInput, LoaderSpin } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToastNavigate } from "@/utils/toastNavigate";
import {
  storeDistributionSellerBalancesWithFiles,
  getSellerBalancesSimplified,
  getFullDistributionPath,
  exportSellerDistributionTemplate,
  importSellerDistributionPreview,
  confirmImportSellerDistribution,
} from "@/Api/distributions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";

// Nova estrutura simplificada do backend
interface SimplifiedSellerBalance {
  current_balance: string;
  held_balance: string;
  total_distributed: string;
  consumed_quantity: string;
}

// Interfaces para detalhes de distribuição
interface DistributionFile {
  id: number;
  name: string | null;
  file_name: string;
  file_url: string;
}

interface DistributionOriginDestination {
  type: 'admin' | 'unity' | 'seller';
  id?: number;
  name: string;
}

interface DistributionDetail {
  distribution_id: number;
  sale_id?: number | null;
  origin: DistributionOriginDestination | null;
  destination: DistributionOriginDestination | null;
  unity_id: number;
  unity_name: string;
  from_unity_id?: number;
  from_unity_name?: string;
  seller_id?: number;
  seller_name?: string;
  from_seller_id?: number;
  from_seller_name?: string;
  created_by: {
    id: number;
    name: string;
  };
  updated_by: {
    id: number;
    name: string;
  };
  distribution_type: {
    id: number;
    name: string;
  };
  status: {
    id: number;
    name: string;
  };
  contest_product_balance: {
    current_balance: string;
    held_balance: string;
  };
  quota_quantity: number;
  created_at: string;
  processed_at?: string;
  files?: DistributionFile[];
}

interface DistributionDetailsResponse {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: number;
    quota_total_distributed: number;
  };
  distributions: DistributionDetail[];
}

interface SimplifiedSeller {
  id: number;
  name: string;
  unity_id?: number;
  contest_product_balance: SimplifiedSellerBalance;
}

interface UnityOption {
  id: number;
  name: string;
}

interface SimplifiedProduct {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: string;
    quota_total_distributed: number;
  };
  sellers: SimplifiedSeller[];
}

interface Product {
  id: number;
  product_name: string;
  product_classification: {
    quota_quantity: number;
    sale_price: number;
  };
  lottery_id: number;
}

const schema = z.object({
  contest_id: z.number(),
  lottery_id: z.number(),
  contest_product_classification_id: z.number(),
  sellers: z.array(
    z.object({
      id: z.number(),
      quota_quantity: z.number().min(0, "Quantidade mínima é 0"),
    })
  ),
});

type SchemaType = z.infer<typeof schema>;

// Componente para modal de detalhes da distribuição
const DistributionDetailsModal = ({
  isOpen,
  onClose,
  contestId,
  productId,
  productName,
}: {
  isOpen: boolean;
  onClose: () => void;
  contestId: string;
  productId: number;
  productName: string;
}) => {
  const [details, setDetails] = useState<DistributionDetailsResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number | null>(null);
  const { toast } = useToastNavigate();

  useEffect(() => {
    if (isOpen && contestId && productId) {
      fetchDetails();
      setSelectedTypeFilter(null);
    }
  }, [isOpen, contestId, productId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await getFullDistributionPath(contestId, productId);
      if (response.data?.status) {
        // Filtrar apenas tipos 2 (Unidade->Vendedor) e 4 (Vendedor->Vendedor)
        const allDistributions = response.data.response;
        const filteredDistributions = {
          ...allDistributions,
          distributions: allDistributions.distributions.filter(
            (d: DistributionDetail) => [2, 4, 7].includes(d.distribution_type.id)
          ),
        };
        setDetails(filteredDistributions);
      } else {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível carregar os detalhes da distribuição.",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Erro ao carregar os detalhes da distribuição.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusId: number, distributionTypeId?: number) => {
    // Pré-venda aceita automaticamente tem cor diferente
    if (distributionTypeId === 7 && statusId === 2) {
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200";
    }
    switch (statusId) {
      case 1:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case 2:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case 3:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getDistributionTypeColor = (typeId: number) => {
    switch (typeId) {
      case 2: // Unidade -> Vendedor
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case 4: // Vendedor -> Vendedor
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200";
      case 7: // Pré-venda
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getOriginDestinationLabel = (dist: DistributionDetail) => {
    if (!dist.origin || !dist.destination) return null;

    return (
      <div className="flex items-center gap-2 text-sm">
        <span className={cn(
          "px-2 py-0.5 rounded text-xs font-medium",
          dist.origin.type === 'unity' ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200" :
          "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
        )}>
          {dist.origin.name}
        </span>
        <span className="text-gray-400">→</span>
        <span className={cn(
          "px-2 py-0.5 rounded text-xs font-medium",
          "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
        )}>
          {dist.destination.name}
        </span>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-0">
          <DialogTitle className="text-xl">Histórico de Distribuição</DialogTitle>
          <DialogDescription className="sr-only">{productName}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <LoaderSpin />
            </div>
          ) : details ? (
            <div className="space-y-4">
              {/* Header melhorado */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {productName}
                    </h3>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 shadow-sm">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {details.contest_product_classification.quota_total_quantity}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Recebidas
                      </p>
                    </div>
                    <div className="text-center px-4 py-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm">
                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {details.distributions.reduce((sum, d) => sum + d.quota_quantity, 0)}
                      </p>
                      <p className="text-xs text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                        Distribuídas
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros dos tipos de distribuição */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Filtrar por tipo:</span>
                <button
                  onClick={() => setSelectedTypeFilter(null)}
                  className={cn(
                    "px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer border",
                    selectedTypeFilter === null
                      ? "bg-slate-700 text-white border-slate-700 dark:bg-slate-200 dark:text-slate-900"
                      : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600"
                  )}
                >
                  Todos ({details.distributions.length})
                </button>
                {[
                  { id: 2, label: "Unidade → Vendedor" },
                  { id: 4, label: "Vendedor → Vendedor" },
                ].map((type) => {
                  const count = details.distributions.filter(d => d.distribution_type.id === type.id).length;
                  if (count === 0) return null;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedTypeFilter(selectedTypeFilter === type.id ? null : type.id)}
                      className={cn(
                        "px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer border",
                        selectedTypeFilter === type.id
                          ? "ring-2 ring-offset-1 ring-slate-500"
                          : "hover:opacity-80",
                        getDistributionTypeColor(type.id)
                      )}
                    >
                      {type.label} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                {(() => {
                  const filteredDistributions = selectedTypeFilter
                    ? details.distributions.filter(d => d.distribution_type.id === selectedTypeFilter)
                    : details.distributions;

                  return (
                    <>
                      <h3 className="font-semibold text-lg">
                        Caminho das Distribuições ({filteredDistributions.length} de {details.distributions.length} registros)
                      </h3>
                      {filteredDistributions.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>De → Para</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Qtd</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Criado por</TableHead>
                                <TableHead>Data</TableHead>
                                <TableHead>ID Venda</TableHead>
                                <TableHead className="text-center">Arquivos</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredDistributions.map((dist) => (
                                <TableRow
                                  key={dist.distribution_id}
                                  className={cn(
                                    dist.distribution_type.id === 7 && "bg-yellow-50 border-l-4 border-l-yellow-500"
                                  )}
                                >
                                  <TableCell className="min-w-[200px]">
                                    {getOriginDestinationLabel(dist)}
                                  </TableCell>
                                  <TableCell>
                                    <span
                                      className={cn(
                                        "px-2 py-1 rounded text-xs font-medium whitespace-nowrap",
                                        getDistributionTypeColor(dist.distribution_type.id)
                                      )}
                                    >
                                      {dist.distribution_type.name}
                                    </span>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {dist.quota_quantity}
                                  </TableCell>
                                  <TableCell>
                                    <span
                                      className={cn(
                                        "px-2 py-1 rounded-full text-xs font-medium text-center",
                                        getStatusColor(dist.status.id, dist.distribution_type.id)
                                      )}
                                    >
                                      {dist.distribution_type.id === 7 && dist.status.id === 2 ? (
                                        <>Aceito<br/>Automaticamente</>
                                      ) : (
                                        dist.status.name
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {dist.created_by.name}
                                  </TableCell>
                                  <TableCell className="text-sm whitespace-nowrap">
                                    {new Date(dist.created_at).toLocaleString("pt-BR", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </TableCell>
                                  <TableCell>
                                    {dist.sale_id ? (
                                      <Link
                                        to={`/vendas?sale_id=${dist.sale_id}`}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm font-medium whitespace-nowrap"
                                        title="Ir para a venda"
                                      >
                                        #{dist.sale_id}
                                        <ExternalLink className="h-3 w-3" />
                                      </Link>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {dist.files && dist.files.length > 0 ? (
                                      dist.files.length === 1 ? (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                          onClick={() => window.open(dist.files![0].file_url, "_blank")}
                                          title={dist.files[0].file_name || "Baixar arquivo"}
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                      ) : (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                              title={`${dist.files.length} arquivos`}
                                            >
                                              <Download className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            {dist.files.map((file) => (
                                              <DropdownMenuItem
                                                key={file.id}
                                                onClick={() => window.open(file.file_url, "_blank")}
                                                className="cursor-pointer"
                                              >
                                                <Download className="h-4 w-4 mr-2" />
                                                {file.file_name || `Arquivo ${file.id}`}
                                              </DropdownMenuItem>
                                            ))}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      )
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          {selectedTypeFilter
                            ? "Nenhuma distribuição deste tipo encontrada."
                            : "Nenhuma distribuição registrada para este produto."}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Nenhum detalhe disponível.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TableData = ({
  dataApi,
  pageActions,
  unityId,
}: {
  dataApi: any;
  pageActions: any;
  unityId: number;
}) => {
  const [searchParams] = useSearchParams();
  const contestId = searchParams.get("q");
  const showBtnRefs = useRef<{ [key: string]: HTMLElement }>({});
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (contestId) {
      const decodedId = atob(contestId);
      const contest = dataApi?.data.find(
        (item) => item.id === Number(decodedId)
      );
      if (contest && showBtnRefs.current[contest.uuid]) {
        const button =
          showBtnRefs.current[contest.uuid].querySelector("button");
        if (button) {
          button.click();
        }
      }
    }
  }, [contestId, dataApi?.data]);

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Data de validade</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {dataApi?.data.map((item) => (
            <TableRow key={item?.id}>
              <TableCell className="truncate max-w-80">{item?.name}</TableCell>
              <TableCell>
                <span
                  className="px-2 py-1 rounded text-white text-xs font-medium"
                  style={{ backgroundColor: item?.lottery?.color || "#666" }}
                >
                  {item?.lottery?.name}
                </span>
              </TableCell>
              <TableCell>
                {item?.validation_date
                  ? Mask.transformMaskBirthdayInBr(item?.validation_date)
                  : "Não informado"}
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <div
                    ref={(el) => el && (showBtnRefs.current[item.uuid] = el)}
                  >
                    <If condition={hasPermission("create-distributions")}>
                      <SharingBtn
                        labels={{ title: "Distribuir para Vendedores" }}
                        params={{
                          func: getShowContests,
                          funcParams: item?.uuid,
                        }}
                        className="sm:max-w-[700px]"
                        render={(responseApi: any) => (
                          <InsideContestModal
                            contestData={responseApi}
                            unityId={unityId}
                          />
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}

          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

function InsideContestModal({
  contestData,
  unityId,
}: {
  contestData: any;
  unityId: number;
}) {
  const { toast } = useToastNavigate();
  const [simplifiedData, setSimplifiedData] = useState<SimplifiedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [distributingProductId, setDistributingProductId] = useState<
    number | null
  >(null);
  const [loadingButton, setLoadingButton] = useState(false);
  const [quotaStates, setQuotaStates] = useState<{
    [key: string]: { sellerId: number; quota: number }[];
  }>({});
  // Ref para armazenar as alterações do usuário (não é resetado pelo fetchPage)
  const userChangesRef = useRef<{
    [productId: string]: { sellerId: number; quota: number }[];
  }>({});
  const [confirmDialogProduct, setConfirmDialogProduct] = useState<any>(null);
  const [showDistributeAllDialog, setShowDistributeAllDialog] = useState(false);
  // Snapshot dos dados a distribuir quando o dialog é aberto
  const [distributeAllSnapshot, setDistributeAllSnapshot] = useState<{
    [productId: string]: { sellerId: number; quota: number }[];
  } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesAll, setSelectedFilesAll] = useState<File[]>([]);
  const [isDistributingAll, setIsDistributingAll] = useState(false);
  const [copiedDistribution, setCopiedDistribution] = useState<
    | {
        sellerId: number;
        quota: number;
      }[]
    | null
  >(null);
  // Novos states para o modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState<{
    id: number;
    name: string;
  } | null>(null);
  // State para filtro de unidades
  const [unities, setUnities] = useState<UnityOption[]>([]);
  const [selectedUnityFilter, setSelectedUnityFilter] = useState<string>("all");
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
  } | null>(null);
  // Filtro por produto
  const [productFilter, setProductFilter] = useState("");
  // Import Excel states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isConfirmingImport, setIsConfirmingImport] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchPage = async (page: number, filter?: string) => {
    setIsLoading(true);
    try {
      // Buscar unidades para o filtro (só na primeira vez)
      if (unities.length === 0) {
        const unitiesResponse = await getSelectUnities();
        if (unitiesResponse.data?.status) {
          setUnities(unitiesResponse.data.response || []);
        }
      }

      const searchFilter = filter !== undefined ? filter : productFilter;
      const productName = searchFilter ? `B${searchFilter}` : undefined;
      const response = await getSellerBalancesSimplified(
        String(contestData.id),
        page,
        100,
        productName
      );

      if (response.data?.status) {
        const responseData = response.data.response;

        // Verificar se a resposta é paginada (tem .data) ou não paginada (array direto)
        const isPaginated = responseData && typeof responseData === 'object' && 'data' in responseData;
        const data = isPaginated
          ? (responseData.data as SimplifiedProduct[])
          : (Array.isArray(responseData) ? responseData as SimplifiedProduct[] : []);

        setSimplifiedData(data);

        if (isPaginated) {
          setPaginationInfo({
            current_page: responseData.current_page,
            per_page: responseData.per_page,
            total: responseData.total,
            last_page: responseData.last_page,
            from: responseData.from,
            to: responseData.to,
          });
        } else {
          // Sem paginação - mostrar todos como página única
          setPaginationInfo({
            current_page: 1,
            per_page: data.length,
            total: data.length,
            last_page: 1,
            from: 1,
            to: data.length,
          });
        }

        // Acumular quotaStates - manter alterações de outras páginas
        setQuotaStates((prev) => {
          const newStates = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            if (!newStates[productId]) {
              newStates[productId] = product.sellers.map((seller) => ({
                sellerId: seller.id,
                quota: 0,
              }));
            }
          });
          return newStates;
        });

      }
    } catch (error) {
      console.error("Erro ao buscar dados simplificados:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar as distribuições.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(currentPage);
  }, [contestData.id, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (paginationInfo?.last_page || 1)) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    setProductFilter(numericValue);
  };

  const handleFilterSearch = () => {
    setCurrentPage(1);
    fetchPage(1, productFilter);
  };

  const handleFilterClear = () => {
    setProductFilter("");
    setCurrentPage(1);
    fetchPage(1, "");
  };

  // Export Excel template
  const handleExportTemplate = async () => {
    setIsExporting(true);
    try {
      const filterUnityId = selectedUnityFilter !== "all" ? parseInt(selectedUnityFilter) : undefined;
      const blob = await exportSellerDistributionTemplate(String(contestData.id), filterUnityId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const unityName = filterUnityId ? unities.find(u => u.id === filterUnityId)?.name : "";
      const fileName = `distribuicao_vendedores_${contestData.lottery?.name}_${contestData.name}${unityName ? `_${unityName}` : ""}_${new Date().toISOString().split("T")[0]}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        description: "Modelo Excel exportado com sucesso!",
        action: <Download className="w-4 h-4 text-green-600" />,
      });
    } catch (error) {
      console.error("Erro ao exportar template:", error);
      toast({
        variant: "destructive",
        title: "Erro ao exportar",
        description: "Não foi possível exportar o modelo Excel.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Import Excel file preview
  const handleImportFile = async (file: File) => {
    setImportFile(file);
    setIsImporting(true);
    setImportPreview(null);
    try {
      const filterUnityId = selectedUnityFilter !== "all" ? parseInt(selectedUnityFilter) : undefined;
      const response = await importSellerDistributionPreview(String(contestData.id), file, filterUnityId);
      if (response.data?.status) {
        setImportPreview(response.data.response);
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao processar arquivo",
          description: response.data?.message || "O arquivo não pôde ser processado.",
        });
      }
    } catch (error: any) {
      console.error("Erro ao importar arquivo:", error);
      toast({
        variant: "destructive",
        title: "Erro ao importar",
        description: error?.response?.data?.message || "Não foi possível processar o arquivo Excel.",
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Calcula o total de registros da importação
  const getImportTotalRecords = (preview: any) => {
    if (!preview?.distributions) return 0;
    return preview.distributions.reduce((total: number, dist: any) => {
      return total + (dist.sellers?.length || 0);
    }, 0);
  };

  const MAX_IMPORT_RECORDS = 500;

  // Confirm import
  const handleConfirmImport = async () => {
    if (!importPreview || importFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo de comprovação.",
      });
      return;
    }

    const totalRecords = getImportTotalRecords(importPreview);
    if (totalRecords > MAX_IMPORT_RECORDS) {
      toast({
        variant: "destructive",
        title: "Importação muito grande",
        description: `A importação possui ${totalRecords} registros. O limite é ${MAX_IMPORT_RECORDS} registros por vez. Por favor, divida em arquivos menores.`,
      });
      return;
    }

    setIsConfirmingImport(true);
    try {
      const filterUnityId = selectedUnityFilter !== "all" ? parseInt(selectedUnityFilter) : unityId;
      const response = await confirmImportSellerDistribution(
        String(contestData.id),
        filterUnityId,
        importPreview.distributions,
        importFiles
      );
      if (response.data?.status) {
        toast({
          description: "Distribuição importada com sucesso!",
          action: <Check className="w-4 h-4 text-green-600" />,
        });
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview(null);
        setImportFiles([]);
        // Refresh data
        await fetchPage(currentPage);
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao confirmar importação",
          description: response.data?.message || "Não foi possível confirmar a importação.",
        });
      }
    } catch (error: any) {
      console.error("Erro ao confirmar importação:", error);
      toast({
        variant: "destructive",
        title: "Erro ao confirmar",
        description: error?.response?.data?.message || "Não foi possível confirmar a importação.",
      });
    } finally {
      setIsConfirmingImport(false);
    }
  };

  function getDistributedQuota(productId: number, sellerId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    const seller = product?.sellers.find((s) => s.id === sellerId);
    return parseInt(seller?.contest_product_balance.total_distributed || "0");
  }

  function getQuotaValidationInfo(
    productId: number,
    currentInputs: { sellerId: number; quota: number }[]
  ) {
    const totalInputQuotas = currentInputs.reduce(
      (sum, item) => sum + item.quota,
      0
    );
    const availableBalance = getAvailableBalance(productId);
    const withHeldBalance = getWithHeldBalance(productId);

    const isOverLimit = totalInputQuotas > availableBalance;
    const remaining = availableBalance - totalInputQuotas;

    return {
      totalInputQuotas,
      availableBalance,
      withHeldBalance,
      isOverLimit,
      remaining,
      canProceed: !isOverLimit,
    };
  }

  function getAvailableBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return 0;

    // Saldo disponível é a diferença entre total e distribuído
    const totalQuotas = parseInt(
      product.contest_product_classification.quota_total_quantity
    );
    const totalDistributed =
      product.contest_product_classification.quota_total_distributed;
    // Nunca negativo: inconsistência de dados não deve travar/confundir a distribuição
    return Math.max(0, totalQuotas - totalDistributed);
  }

  function getWithHeldBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return 0;

    // Somar todos os held_balance dos vendedores
    return product.sellers.reduce((sum, seller) => {
      return sum + parseInt(seller.contest_product_balance.held_balance);
    }, 0);
  }

  function handleQuotaChange(
    productId: number,
    sellerId: number,
    quota: number
  ) {
    setQuotaStates((prev) => ({
      ...prev,
      [productId]: prev[productId]?.map((item) =>
        item.sellerId === sellerId ? { ...item, quota } : item
      ) || [{ sellerId, quota }],
    }));

    // Também salva no ref para garantir persistência
    if (!userChangesRef.current[productId]) {
      userChangesRef.current[productId] = [];
    }
    const existingIndex = userChangesRef.current[productId].findIndex(
      (item) => item.sellerId === sellerId
    );
    if (existingIndex >= 0) {
      userChangesRef.current[productId][existingIndex].quota = quota;
    } else {
      userChangesRef.current[productId].push({ sellerId, quota });
    }
  }

  function handleCopyDistribution(productId: number) {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return;

    const distributionToCopy = product.sellers.map((seller) => ({
      sellerId: seller.id,
      quota: parseInt(seller.contest_product_balance.total_distributed || "0"),
    }));
    setCopiedDistribution(distributionToCopy);
    toast({
      description: "Distribuição copiada com sucesso!",
      action: <Copy className="w-4 h-4" />,
    });
  }

  function handleCopyCurrentDistribution(productId: number) {
    const currentQuotas = quotaStates[productId] || [];
    const nonZeroQuotas = currentQuotas.filter((quota) => quota.quota > 0);

    if (nonZeroQuotas.length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhuma cota foi definida para copiar.",
      });
      return;
    }

    setCopiedDistribution(
      nonZeroQuotas.map((quota) => ({
        sellerId: quota.sellerId,
        quota: quota.quota,
      }))
    );

    const totalQuotas = nonZeroQuotas.reduce(
      (sum, quota) => sum + quota.quota,
      0
    );
    toast({
      description: `Distribuição atual copiada: ${totalQuotas} cotas em ${nonZeroQuotas.length} vendedor(es)`,
      action: <Copy className="w-4 h-4" />,
    });
  }

  function handlePasteDistribution(productId: number) {
    if (!copiedDistribution || copiedDistribution.length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhuma distribuição copiada encontrada.",
      });
      return;
    }

    setQuotaStates((prev) => ({
      ...prev,
      [productId]: prev[productId].map((item) => {
        const match = copiedDistribution.find(
          (q) => q.sellerId === item.sellerId
        );
        return match ? { ...item, quota: match.quota } : item;
      }),
    }));

    // Também salva no ref para garantir persistência
    if (!userChangesRef.current[productId]) {
      userChangesRef.current[productId] = [];
    }
    copiedDistribution.forEach((copied) => {
      const existingIndex = userChangesRef.current[productId].findIndex(
        (item) => item.sellerId === copied.sellerId
      );
      if (existingIndex >= 0) {
        userChangesRef.current[productId][existingIndex].quota = copied.quota;
      } else {
        userChangesRef.current[productId].push({ sellerId: copied.sellerId, quota: copied.quota });
      }
    });

    toast({
      description: "Distribuição colada com sucesso!",
      action: <Clipboard className="w-4 h-4" />,
    });
  }

  function handleShowDetails(productId: number, productName: string) {
    setSelectedProductDetails({ id: productId, name: productName });
    setShowDetailsModal(true);
  }

  function getFilteredSellers(sellers: SimplifiedSeller[]): SimplifiedSeller[] {
    if (selectedUnityFilter === "all") {
      return sellers;
    }
    // Verifica se os sellers têm unity_id (backend atualizado)
    // Se não tiverem, retorna todos os sellers
    const hasUnityId = sellers.some((seller) => seller.unity_id !== undefined);
    if (!hasUnityId) {
      return sellers;
    }
    return sellers.filter(
      (seller) => seller.unity_id === parseInt(selectedUnityFilter)
    );
  }

  async function handleSubmitDistribution(product: Product) {
    if (selectedFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo para confirmar a distribuição.",
      });
      return;
    }

    setDistributingProductId(product.id);
    const localQuotas = quotaStates[product.id] || [];

    // Para vendedores, consideramos qualquer valor > 0 como uma distribuição válida
    const modifiedSellers = localQuotas.filter((currentQuota) => {
      return currentQuota.quota > 0;
    });

    if (modifiedSellers.length === 0) {
      toast({
        description: "Nenhuma alteração foi detectada para enviar.",
      });
      setDistributingProductId(null);
      return;
    }

    const dataToSubmit: SchemaType & { unity_id: number } = {
      contest_id: contestData.id,
      lottery_id: product.lottery_id,
      unity_id: unityId,
      contest_product_classification_id: product.id,
      sellers: modifiedSellers.map((sq) => ({
        id: sq.sellerId,
        quota_quantity: sq.quota,
      })),
    };

    const parseResult = schema.safeParse(dataToSubmit);
    if (!parseResult.success) {
      toast({
        variant: "destructive",
        description: "Verifique os campos de distribuição.",
      });
      setDistributingProductId(null);
      return;
    }

    const validationInfo = getQuotaValidationInfo(product.id, localQuotas);

    if (validationInfo.isOverLimit) {
      const excess =
        validationInfo.totalInputQuotas - validationInfo.availableBalance;
      toast({
        variant: "destructive",
        title: "Distribuição bloqueada",
        description: `Não é possível prosseguir. Você está tentando distribuir ${
          validationInfo.totalInputQuotas
        } cotas, mas há apenas ${
          validationInfo.availableBalance
        } disponíveis. Excesso: ${excess} cotas.${
          validationInfo.withHeldBalance > 0
            ? ` (${validationInfo.withHeldBalance} em processamento)`
            : ""
        }`,
      });
      setDistributingProductId(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("contest_id", dataToSubmit.contest_id.toString());
      formData.append("lottery_id", dataToSubmit.lottery_id.toString());
      formData.append("unity_id", dataToSubmit.unity_id.toString());
      formData.append("contest_product_classification_id", dataToSubmit.contest_product_classification_id.toString());

      dataToSubmit.sellers.forEach((seller, index) => {
        formData.append(`sellers[${index}][id]`, seller.id.toString());
        formData.append(`sellers[${index}][quota_quantity]`, seller.quota_quantity.toString());
      });

      selectedFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const { ok } = await storeDistributionSellerBalancesWithFiles(formData);
      if (!ok) {
        throw new Error("Erro ao registrar distribuição");
      }
      setSelectedFiles([]);
      toast({
        description: `Distribuição salva com sucesso (${
          modifiedSellers.length
        } vendedor${modifiedSellers.length > 1 ? "es" : ""} atualizado${
          modifiedSellers.length > 1 ? "s" : ""
        })`,
        action: <Check color="#1edc2b" />,
      });

      // Recarregar dados simplificados após salvar
      const response = await getSellerBalancesSimplified(
        String(contestData.id)
      );
      if (response.data?.status) {
        const data = response.data.response as SimplifiedProduct[];
        setSimplifiedData(data);

        // Atualizar states após salvar - reset inputs to 0
        const newQuotaStates: {
          [key: string]: { sellerId: number; quota: number }[];
        } = {};

        data.forEach((product) => {
          const productId = product.contest_product_classification.id;
          const resetSellerQuotas = product.sellers.map((seller) => ({
            sellerId: seller.id,
            quota: 0, // Reset inputs to 0 after save
          }));

          newQuotaStates[productId] = [...resetSellerQuotas];
        });

        setQuotaStates(newQuotaStates);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        description:
          error instanceof Error ? error.message : "Erro ao criar distribuição",
      });
    } finally {
      setDistributingProductId(null);
    }
  }
  const handleOpenDistributeAllDialog = () => {
    const snapshot: { [productId: string]: { sellerId: number; quota: number }[] } = {};

    // 1. Primeiro, pega do userChangesRef (alterações de páginas anteriores)
    Object.entries(userChangesRef.current).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    // 2. Depois, pega do quotaStates atual (página atual) - sobrescreve se já existir
    Object.entries(quotaStates).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    setDistributeAllSnapshot(snapshot);
    setShowDistributeAllDialog(true);
  };

  async function handleDistributeAll() {
    if (selectedFilesAll.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo para confirmar a distribuição.",
      });
      return;
    }

    if (!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhum produto para distribuir.",
      });
      return;
    }

    setLoadingButton(true);
    setIsDistributingAll(true);
    setShowDistributeAllDialog(false);

    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    // Usar o snapshot capturado quando o dialog foi aberto
    const allProductIds = Object.keys(distributeAllSnapshot);

    for (const productIdStr of allProductIds) {
      try {
        const productId = parseInt(productIdStr);
        const sellersToDistribute = distributeAllSnapshot[productId] || [];

        if (sellersToDistribute.length === 0) {
          skippedCount++;
          continue;
        }

        const dataToSubmit: SchemaType & { unity_id: number } = {
          contest_id: contestData.id,
          lottery_id: contestData.lottery?.id,
          unity_id: unityId,
          contest_product_classification_id: productId,
          sellers: sellersToDistribute.map((sq) => ({
            id: sq.sellerId,
            quota_quantity: sq.quota,
          })),
        };

        const parseResult = schema.safeParse(dataToSubmit);
        if (!parseResult.success) {
          errorCount++;
          continue;
        }

        // Só valida limite se o produto está na página atual (simplifiedData)
        const productInCurrentPage = simplifiedData.find(
          (p) => p.contest_product_classification.id === productId
        );
        if (productInCurrentPage) {
          const validationInfo = getQuotaValidationInfo(productId, sellersToDistribute);
          if (validationInfo.isOverLimit) {
            errorCount++;
            continue;
          }
        }

        const formData = new FormData();
        formData.append("contest_id", dataToSubmit.contest_id.toString());
        formData.append("lottery_id", dataToSubmit.lottery_id.toString());
        formData.append("unity_id", dataToSubmit.unity_id.toString());
        formData.append("contest_product_classification_id", dataToSubmit.contest_product_classification_id.toString());

        dataToSubmit.sellers.forEach((seller, index) => {
          formData.append(`sellers[${index}][id]`, seller.id.toString());
          formData.append(`sellers[${index}][quota_quantity]`, seller.quota_quantity.toString());
        });

        selectedFilesAll.forEach((file) => {
          formData.append("files[]", file);
        });

        const { ok } = await storeDistributionSellerBalancesWithFiles(formData);

        if (ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    try {
      setLoadingButton(false);
      // Limpar todos os states após distribuir
      setQuotaStates({});
      setDistributeAllSnapshot(null);
      userChangesRef.current = {};
      // Recarregar dados simplificados após salvar
      await fetchPage(currentPage);
    } catch (error) {
      console.error("Erro ao atualizar distribuições:", error);
    }

    setIsDistributingAll(false);
    setSelectedFilesAll([]);
    setDistributeAllSnapshot(null);

    if (successCount > 0 && errorCount === 0) {
      const message =
        skippedCount > 0
          ? `${successCount} distribuições salvas com sucesso! ${skippedCount} produtos foram ignorados.`
          : `Todas as ${successCount} distribuições foram salvas com sucesso!`;
      toast({
        description: message,
        action: <Check color="#1edc2b" />,
      });
    } else if (successCount > 0 && errorCount > 0) {
      toast({
        description: `${successCount} distribuições salvas com sucesso, ${errorCount} falharam.`,
        action: <Check color="#ff8000" />,
      });
    } else if (skippedCount > 0 && successCount === 0 && errorCount === 0) {
      toast({
        description:
          "Nenhuma distribuição processada. Defina cotas para os produtos primeiro.",
      });
    } else {
      toast({
        variant: "destructive",
        description: "Erro ao salvar todas as distribuições",
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoaderSpin />
      </div>
    );
  }

  return (
    <>
      <div className="h-[70vh] overflow-hidden flex flex-col" style={{ maxHeight: "70vh" }}>
        <div className="w-full h-auto flex-shrink-0">
          {/* Header melhorado do concurso */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 mb-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
                  style={{ backgroundColor: contestData?.lottery?.color || '#023859' }}
                >
                  {contestData?.lottery?.name}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    {contestData?.name}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Validade: {contestData?.validation_date
                      ? Mask.transformMaskBirthdayInBr(contestData?.validation_date)
                      : "Não informado"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    Unidade
                  </span>
                  <Select
                    value={selectedUnityFilter}
                    onValueChange={setSelectedUnityFilter}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as unidades</SelectItem>
                      {unities.map((unity) => (
                        <SelectItem key={unity.id} value={String(unity.id)}>
                          {unity.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleOpenDistributeAllDialog}
                  className={cn("flex gap-2", colors.bg_primary)}
                  disabled={isDistributingAll || isLoading}
                >
                  {isDistributingAll ? (
                    <>
                      Distribuindo... <LoaderSpin />
                    </>
                  ) : (
                    "Distribuir Tudo"
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Botões de Exportar/Importar Excel */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">
              Modelos de Excel:
            </span>
            <Button
              onClick={handleExportTemplate}
              disabled={isExporting}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              {isExporting ? (
                <LoaderSpin />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Exportar Modelo
            </Button>
            <Button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              <Upload className="w-4 h-4" />
              Importar Excel
            </Button>
          </div>

          {/* Filtro por produto */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">B:</span>
              <Input
                type="text"
                placeholder="Ex: 200"
                value={productFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFilterSearch()}
                className="w-24"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleFilterSearch}
              className="flex items-center gap-1"
            >
              <Search className="w-4 h-4" />
              Buscar
            </Button>
            {productFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFilterClear}
              >
                Limpar
              </Button>
            )}
            {paginationInfo && (
              <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                Exibindo {paginationInfo.from}-{paginationInfo.to} de {paginationInfo.total} produtos
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
          <div>
            <Table>
              <TableHeader className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Qtd. Cotas</TableHead>
                  <TableHead>Preço de venda</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                  <TableHead className="text-center">Distribuição</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody striped>
                {simplifiedData?.map((productData) => {
                  const product = {
                    id: productData.contest_product_classification.id,
                    product_name: productData.contest_product_classification.product_name,
                    product_classification: {
                      name: "",
                      quota_quantity: parseInt(productData.contest_product_classification.quota_total_quantity),
                      sale_price: productData.contest_product_classification.sale_price,
                    },
                    lottery_id: contestData.lottery?.id,
                  };
                  const localQuotas = quotaStates[product.id] || [];
                  return (
                    <TableRow key={product.id} className="transition-colors">
                      <TableCell className="align-top">
                        {productData.contest_product_classification.product_name}
                      </TableCell>
                      <TableCell className="text-center align-top">
                        {(() => {
                            const totalQuotas = parseInt(
                              productData.contest_product_classification
                                .quota_total_quantity
                            );
                            const totalDistributed =
                              productData.contest_product_classification
                                .quota_total_distributed;

                            return (
                              <div className="flex flex-col items-center gap-1">
                                <span>{`${totalDistributed} / ${totalQuotas}`}</span>
                                {(() => {
                                  const available = getAvailableBalance(
                                    product.id
                                  );
                                  return (
                                    <>
                                      {available > 0 && (
                                        <span className="text-xs text-red-500 dark:text-red-400 font-medium">
                                          {available} restantes para
                                          distribuição
                                        </span>
                                      )}
                                      {available === 0 &&
                                        totalDistributed < totalQuotas && (
                                          <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                                            Sem saldo disponível
                                          </span>
                                        )}
                                      {totalDistributed === totalQuotas && (
                                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                          Todas as cotas distribuídas
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center align-top">
                          {(() => {
                            const productData = simplifiedData.find(
                              (p) =>
                                p.contest_product_classification.id ===
                                product.id
                            );
                            return Mask.formatCurrency(
                              productData?.contest_product_classification
                                ?.sale_price ||
                                product?.product_classification?.sale_price ||
                                0
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center align-top">
                          <Button
                            onClick={() =>
                              handleShowDetails(
                                product.id,
                                `${product.product_name} - ${product.product_classification?.name}`
                              )
                            }
                            title="Ver detalhes das distribuições"
                            variant="ghost"
                            size="icon"
                            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="border rounded p-2 mb-2 max-h-40 overflow-auto">
                            {(() => {
                              const productData = simplifiedData.find(
                                (p) =>
                                  p.contest_product_classification.id ===
                                  product.id
                              );
                              if (!productData) return null;
                              const filteredSellers = getFilteredSellers(productData.sellers);
                              if (filteredSellers.length === 0) {
                                return (
                                  <div className="text-sm text-gray-500 dark:text-gray-400 py-2 text-center">
                                    Nenhum vendedor encontrado para esta unidade.
                                  </div>
                                );
                              }
                              return filteredSellers.map((seller) => {
                                const quotaValue =
                                  localQuotas.find(
                                    (q) => q.sellerId === seller.id
                                  )?.quota ?? 0;

                                const distributedQuota = getDistributedQuota(
                                  product.id,
                                  seller.id
                                );

                                return (
                                  <div
                                    key={seller.id}
                                    className="flex items-center gap-2 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2"
                                  >
                                    <div className="w-40 text-sm">
                                      <span className="font-medium">
                                        {seller.name}
                                      </span>
                                      {distributedQuota > 0 && (
                                        <span className="text-xs text-blue-600 dark:text-blue-400 ml-2">
                                          ({distributedQuota} distribuída
                                          {distributedQuota > 1 ? "s" : ""})
                                        </span>
                                      )}

                                      {/* Mostrar saldo real do vendedor para este produto */}
                                      <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                        Cotas disponíveis:{" "}
                                        {
                                          seller.contest_product_balance
                                            .current_balance
                                        }
                                      </div>
                                    </div>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={quotaValue}
                                      onChange={(e) =>
                                        handleQuotaChange(
                                          product.id,
                                          seller.id,
                                          parseInt(e.target.value) || 0
                                        )
                                      }
                                      className="w-20"
                                      disabled={
                                        distributingProductId === product.id
                                      }
                                    />
                                  </div>
                                );
                              });
                            })()}
                          </div>

                          {/* Validação e resumo */}
                          {(() => {
                            const validationInfo = getQuotaValidationInfo(
                              product.id,
                              localQuotas
                            );
                            if (validationInfo.totalInputQuotas === 0)
                              return null;

                            return (
                              <div className="mt-2 mb-2 p-2 rounded bg-gray-50 dark:bg-gray-800">
                                <div className="text-sm">
                                  <span className="font-medium">
                                    Total: {validationInfo.totalInputQuotas} /{" "}
                                    {validationInfo.availableBalance}
                                  </span>
                                  {validationInfo.isOverLimit ? (
                                    <div className="text-red-600 dark:text-red-400 text-xs mt-1">
                                      ⚠️ Excesso de{" "}
                                      {validationInfo.totalInputQuotas -
                                        validationInfo.availableBalance}{" "}
                                      cotas sobre o limite disponível
                                      {validationInfo.withHeldBalance > 0 && (
                                        <span className="block">
                                          ({validationInfo.withHeldBalance} em
                                          processamento)
                                        </span>
                                      )}
                                    </div>
                                  ) : validationInfo.remaining > 0 ? (
                                    <div className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                                      💡 Ainda restam {validationInfo.remaining}{" "}
                                      cotas disponíveis para distribuir
                                    </div>
                                  ) : (
                                    <div className="text-green-600 dark:text-green-400 text-xs mt-1">
                                      ✅ Utilizando todo o saldo disponível
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Ações de Distribuição */}
                          <div className="flex flex-col gap-2 mb-4">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Ações de Distribuição
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyDistribution(product.id)}
                              className="flex items-center gap-2 justify-start"
                            >
                              <Copy className="w-4 h-4" />
                              Copiar Distribuição Feita
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleCopyCurrentDistribution(product.id)
                              }
                              className="flex items-center gap-2 justify-start"
                              disabled={distributingProductId === product.id}
                            >
                              <Copy className="w-4 h-4" />
                              Copiar Dados Atuais
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handlePasteDistribution(product.id)
                              }
                              disabled={!copiedDistribution}
                              className="flex items-center gap-2 justify-start"
                            >
                              <Clipboard className="w-4 h-4" />
                              Colar Distribuição
                            </Button>
                          </div>

                          <Button
                            variant="outline"
                            className={cn(
                              "w-full",
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                return validationInfo.isOverLimit
                                  ? "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:border-red-700 dark:text-red-300"
                                  : colors.bg_primary;
                              })()
                            )}
                            disabled={
                              distributingProductId === product.id ||
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                const hasValidChanges = localQuotas.some(
                                  (quota) => quota.quota > 0
                                );
                                return (
                                  validationInfo.isOverLimit || !hasValidChanges
                                );
                              })()
                            }
                            onClick={() => setConfirmDialogProduct(product)}
                          >
                            {distributingProductId === product.id ? (
                              <LoaderSpin />
                            ) : (
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                const hasValidChanges = localQuotas.some(
                                  (quota) => quota.quota > 0
                                );

                                if (validationInfo.isOverLimit) {
                                  return "Distribuição bloqueada (excede limite)";
                                }
                                if (!hasValidChanges) {
                                  return "Defina cotas para distribuir";
                                }
                                return "Fazer distribuição";
                              })()
                            )}
                          </Button>

                          <Dialog
                            open={
                              confirmDialogProduct &&
                              confirmDialogProduct.id === product.id
                            }
                            onOpenChange={(open) =>
                              !open && setConfirmDialogProduct(null)
                            }
                          >
                            <DialogContent
                              className="z-[60]"
                              style={{ zIndex: 60 }}
                            >
                              <DialogHeader>
                                <DialogTitle>
                                  Confirmar Distribuição
                                </DialogTitle>
                                <DialogDescription>
                                  {confirmDialogProduct &&
                                    (() => {
                                      const confirmLocalQuotas =
                                        quotaStates[confirmDialogProduct.id] ||
                                        [];
                                      const confirmTotalQuotas =
                                        confirmLocalQuotas.reduce(
                                          (sum, item) => sum + item.quota,
                                          0
                                        );
                                      return (
                                        <div className="space-y-2">
                                          <p>
                                            Você está prestes a distribuir as
                                            cotas para o produto:
                                          </p>
                                          <p className="font-semibold">
                                            {confirmDialogProduct.product_name}{" "}
                                            -{" "}
                                            {
                                              confirmDialogProduct
                                                .product_classification.name
                                            }
                                          </p>
                                          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                                            <p className="text-sm mb-2">
                                              <strong>
                                                Distribuição por vendedor:
                                              </strong>
                                            </p>
                                            <div className="space-y-1 mb-3">
                                              {confirmLocalQuotas.map(
                                                (quota) => {
                                                  const productData =
                                                    simplifiedData.find(
                                                      (p) =>
                                                        p
                                                          .contest_product_classification
                                                          .id === product.id
                                                    );
                                                  const seller =
                                                    productData?.sellers.find(
                                                      (s) =>
                                                        s.id === quota.sellerId
                                                    );
                                                  const quotaValue =
                                                    quota.quota;
                                                  if (quotaValue > 0) {
                                                    return (
                                                      <div
                                                        key={quota.sellerId}
                                                        className="text-sm"
                                                      >
                                                        <span className="font-medium">
                                                          {seller?.name}
                                                        </span>{" "}
                                                        -{" "}
                                                        <span className="text-blue-600 dark:text-blue-400">
                                                          {quotaValue} cotas
                                                        </span>
                                                      </div>
                                                    );
                                                  }
                                                  return null;
                                                }
                                              )}
                                            </div>
                                            <div className="border-t pt-2">
                                              <p className="text-sm">
                                                <strong>
                                                  Total de cotas a distribuir:
                                                </strong>{" "}
                                                {confirmTotalQuotas}
                                              </p>
                                              <p className="text-sm">
                                                <strong>
                                                  Máximo disponível:
                                                </strong>{" "}
                                                {getAvailableBalance(
                                                  confirmDialogProduct.id
                                                )}
                                              </p>
                                              {getAvailableBalance(
                                                confirmDialogProduct.id
                                              ) > confirmTotalQuotas && (
                                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                                  <strong>Restam:</strong>{" "}
                                                  {getAvailableBalance(
                                                    confirmDialogProduct.id
                                                  ) - confirmTotalQuotas}{" "}
                                                  cotas para distribuir
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          <FileUpload
                                            files={selectedFiles}
                                            onFilesChange={setSelectedFiles}
                                            inputId="file-upload-seller-single"
                                          />
                                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                                            Esta ação não pode ser desfeita.
                                            Deseja continuar?
                                          </p>
                                        </div>
                                      );
                                    })()}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setConfirmDialogProduct(null);
                                    setSelectedFiles([]);
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  onClick={() => {
                                    if (confirmDialogProduct) {
                                      handleSubmitDistribution(
                                        confirmDialogProduct
                                      );
                                      setConfirmDialogProduct(null);
                                    }
                                  }}
                                  className={cn(colors.bg_primary)}
                                  disabled={selectedFiles.length === 0}
                                >
                                  Confirmar Distribuição
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {contestData?.product_classifications?.length < 1 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        Nenhum produto encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

        {/* Paginação */}
        {paginationInfo && paginationInfo.last_page > 1 && (
          <div className="flex justify-between items-center mt-4 pt-4 border-t flex-shrink-0">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Página {paginationInfo.current_page} de {paginationInfo.last_page}
            </span>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    className={cn("h-8", colors.bg_primary)}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink
                    href="#"
                    className={cn(
                      "size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                    )}
                  >
                    <span className="pointer-events-none dark:text-[#F4F4F5]">
                      {paginationInfo.current_page}
                    </span>
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink
                    href="#"
                    className="text-black h-8 hover:bg-stone-950/10"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(paginationInfo.last_page);
                    }}
                  >
                    <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    className={cn("h-8", colors.bg_primary)}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      <Dialog
          open={showDistributeAllDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowDistributeAllDialog(false);
              setDistributeAllSnapshot(null);
            }
          }}
        >
          <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
            <DialogHeader>
              <DialogTitle>Confirmar Distribuicao Geral</DialogTitle>
              <DialogDescription>
                <div className="space-y-2">
                  <p>
                    Voce esta prestes a distribuir as cotas para{" "}
                    <strong>todos os produtos</strong> do concurso:
                  </p>
                  <p className="font-semibold">
                    {contestData?.name} - {contestData?.lottery?.name}
                  </p>
                  {paginationInfo && paginationInfo.total > paginationInfo.per_page && (
                    <p className="text-xs text-orange-600 dark:text-orange-400">
                      Inclui dados de todas as {paginationInfo.last_page} paginas
                    </p>
                  )}
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <p className="text-sm mb-2">
                      <strong>Produtos que serao distribuidos:</strong>
                    </p>
                    <div
                      className="h-40 w-full overflow-y-auto border rounded p-2"
                      style={{ maxHeight: "160px" }}
                    >
                      <div className="space-y-1">
                        {distributeAllSnapshot && Object.entries(distributeAllSnapshot).map(
                          ([productIdStr, sellerQuotas]) => {
                            const productId = parseInt(productIdStr);
                            const productData = simplifiedData.find(
                              (p) => p.contest_product_classification.id === productId
                            );
                            const totalQuotas = sellerQuotas.reduce(
                              (sum, item) => sum + item.quota,
                              0
                            );
                            const hasQuotas = totalQuotas > 0;

                            if (!hasQuotas) return null;

                            return (
                              <div
                                key={productId}
                                className="text-sm p-3 rounded border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                              >
                                <div className="font-medium mb-2">
                                  {productData?.contest_product_classification.product_name || `Produto #${productId}`}
                                </div>

                                <div className="space-y-1 mb-2">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Distribuicao por vendedor:
                                  </p>
                                  {sellerQuotas.map((quota) => {
                                    const seller = productData?.sellers.find(
                                      (s) => s.id === quota.sellerId
                                    );
                                    if (quota.quota > 0) {
                                      return (
                                        <div
                                          key={quota.sellerId}
                                          className="text-xs"
                                        >
                                          <span className="font-medium">
                                            {seller?.name || `Vendedor #${quota.sellerId}`}
                                          </span>{" "}
                                          -{" "}
                                          <span className="text-blue-600 dark:text-blue-400">
                                            {quota.quota} cotas
                                          </span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>

                                <div className="border-t pt-2 space-y-1">
                                  <div className="text-xs">
                                    <strong>
                                      Total de cotas a distribuir:
                                    </strong>{" "}
                                    {totalQuotas}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                        {(!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) && (
                          <div className="text-sm text-gray-500 text-center py-4">
                            Nenhum produto com cotas definidas
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <FileUpload
                    files={selectedFilesAll}
                    onFilesChange={setSelectedFilesAll}
                    inputId="file-upload-seller-all"
                  />

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                    Esta acao ira processar todos os produtos com cotas
                    definidas. Produtos sem cotas serao ignorados. Deseja
                    continuar?
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDistributeAllDialog(false);
                  setSelectedFilesAll([]);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDistributeAll}
                className={cn(colors.bg_primary)}
                disabled={loadingButton || selectedFilesAll.length === 0}
              >
                {
                  loadingButton ? (
                    <>
                      <LoaderSpin />
                      Distribuindo...
                    </>
                  ) : (
                    "Confirmar Distribuição Geral"
                  )
                }
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Modal de detalhes das distribuições */}
      {selectedProductDetails && (
        <DistributionDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProductDetails(null);
          }}
          contestId={String(contestData.id)}
          productId={selectedProductDetails.id}
          productName={selectedProductDetails.name}
        />
      )}

      {/* Modal de Importação Excel */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        if (!open) {
          setShowImportModal(false);
          setImportFile(null);
          setImportPreview(null);
          setImportFiles([]);
        }
      }}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col z-[60]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Importar Distribuição de Vendedores via Excel
            </DialogTitle>
            <DialogDescription>
              Selecione um arquivo Excel com a distribuição preenchida para importar.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Upload de arquivo */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Arquivo Excel</label>
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                  importFile
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-green-400"
                )}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImportFile(file);
                    }
                  }}
                  className="hidden"
                  id="excel-import-seller"
                />
                <label
                  htmlFor="excel-import-seller"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  {importFile ? (
                    <>
                      <FileSpreadsheet className="w-10 h-10 text-green-600" />
                      <span className="text-green-700 dark:text-green-400 font-medium">
                        {importFile.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        Clique para trocar o arquivo
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">
                        Clique para selecionar um arquivo Excel
                      </span>
                      <span className="text-xs text-gray-400">
                        Formatos aceitos: .xlsx, .xls
                      </span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Loading */}
            {isImporting && (
              <div className="flex items-center justify-center py-8">
                <LoaderSpin />
                <span className="ml-2 text-gray-600">Processando arquivo...</span>
              </div>
            )}

            {/* Preview */}
            {importPreview && !isImporting && (
              <div className="space-y-4">
                {/* Resumo */}
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Resumo da Importação</h4>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Concurso + Loteria */}
                    <div className="flex items-center gap-3">
                      <div
                        className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold"
                        style={{ backgroundColor: contestData?.lottery?.color || '#666' }}
                      >
                        {importPreview.contest?.lottery}
                      </div>
                      <span className="text-lg font-bold">{importPreview.contest?.name}</span>
                    </div>
                    {/* Totais */}
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Total de Produtos:</span>
                        <span className="font-medium ml-1">{importPreview.summary?.total_products}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total de Cotas:</span>
                        <span className="font-medium text-green-600 ml-1">{importPreview.summary?.total_quotas}</span>
                      </div>
                      {importPreview.summary?.total_errors > 0 && (
                        <div>
                          <span className="text-gray-500">Erros:</span>
                          <span className="font-medium text-red-600 ml-1">{importPreview.summary?.total_errors}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Alerta de importação muito grande */}
                {getImportTotalRecords(importPreview) > MAX_IMPORT_RECORDS && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h4 className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      Importação muito grande
                    </h4>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      A importação possui <strong>{getImportTotalRecords(importPreview)}</strong> registros (vendedores x produtos).
                      O limite é <strong>{MAX_IMPORT_RECORDS}</strong> registros por vez.
                      Por favor, divida o arquivo Excel em partes menores para evitar timeout.
                    </p>
                  </div>
                )}

                {/* Erros */}
                {importPreview.errors && importPreview.errors.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h4 className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      Erros Encontrados
                    </h4>
                    <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 max-h-32 overflow-y-auto">
                      {importPreview.errors.map((error: string, idx: number) => (
                        <li key={idx}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {importPreview.warnings && importPreview.warnings.length > 0 && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      Avisos
                    </h4>
                    <ul className="text-sm text-yellow-600 dark:text-yellow-400 space-y-1 max-h-32 overflow-y-auto">
                      {importPreview.warnings.map((warning: string, idx: number) => (
                        <li key={idx}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Lista de distribuições */}
                {importPreview.distributions && importPreview.distributions.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 dark:bg-slate-700 px-4 py-2 font-medium text-sm">
                      Distribuições a serem importadas
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produto</TableHead>
                            <TableHead>Vendedores</TableHead>
                            <TableHead className="text-right">Total Cotas</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.distributions.map((dist: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">
                                {dist.product_name}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {dist.sellers?.map((seller: any) => (
                                    <span
                                      key={seller.id}
                                      className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded"
                                    >
                                      {seller.name}: {seller.quota_quantity}
                                    </span>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium text-green-600">
                                {dist.total_distributed}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Upload de comprovantes */}
                {importPreview.distributions && importPreview.distributions.length > 0 && (
                  <div className="border-t pt-4">
                    <FileUpload
                      files={importFiles}
                      onFilesChange={setImportFiles}
                      inputId="file-upload-import-seller"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportPreview(null);
                setImportFiles([]);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={
                !importPreview ||
                importPreview.distributions?.length === 0 ||
                importFiles.length === 0 ||
                isConfirmingImport ||
                getImportTotalRecords(importPreview) > MAX_IMPORT_RECORDS
              }
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isConfirmingImport ? (
                <>
                  <LoaderSpin />
                  Importando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirmar Importação
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
